import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createAsyncStorage } from '@/lib/asyncStorage'
import { chromeStorage } from '@/lib/storage'

export interface ClockLocation {
  id: string
  city: string
  timezone: string
}

export interface WeatherLocation {
  id: string
  city: string
  latitude: number
  longitude: number
}

export interface SettingsState {
  theme: 'light' | 'dark'
  language: 'fa' | 'en'
  calendar: 'jalali' | 'gregorian'
  weekStart: 'saturday' | 'sunday'
  plannerName: string
  clockLocations: ClockLocation[]
  weatherLocations: WeatherLocation[]
  incomeCategories: string[]
  expenseCategories: string[]
  toggleTheme: () => void
  setLanguage: (lang: 'fa' | 'en') => void
  setCalendar: (calendar: 'jalali' | 'gregorian') => void
  setPlannerName: (name: string) => void
  addClockLocation: (location: ClockLocation) => void
  removeClockLocation: (id: string) => void
  addWeatherLocation: (location: WeatherLocation) => void
  removeWeatherLocation: (id: string) => void
  addIncomeCategory: (category: string) => void
  removeIncomeCategory: (category: string) => void
  addExpenseCategory: (category: string) => void
  removeExpenseCategory: (category: string) => void
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      theme: 'dark',
      language: 'fa',
      calendar: 'jalali',
      weekStart: 'saturday',
      plannerName: 'Moji Planner',
      clockLocations: [{ id: 'local', city: 'Local Time', timezone: 'local' }],
      weatherLocations: [],
      incomeCategories: ['حقوق', 'سرمایه‌گذاری', 'دیگر'],
      expenseCategories: ['غذا', 'حمل‌ونقل', 'تفریح', 'آموزش', 'دیگر'],

      toggleTheme: () => {
        set((state) => {
          const newTheme = state.theme === 'light' ? 'dark' : 'light'
          console.log('🎨 Toggling theme:', state.theme, '->', newTheme)
          // Apply theme to DOM immediately
          if (typeof window !== 'undefined' && document.documentElement.classList) {
            if (newTheme === 'dark') {
              document.documentElement.classList.add('dark')
            } else {
              document.documentElement.classList.remove('dark')
            }
          }
          return { theme: newTheme }
        })
      },

      setLanguage: (lang) => {
        console.log('🌍 Setting language:', lang)
        set({ language: lang })
      },

      setCalendar: (calendar) => {
        console.log('📅 Setting calendar:', calendar)
        set({ calendar })
      },

      setPlannerName: (name) => {
        console.log('📝 Setting planner name:', name)
        set({ plannerName: name })
      },

      addClockLocation: (location) => {
        set((state) => ({
          clockLocations: [...state.clockLocations, location],
        }))
      },

      removeClockLocation: (id) => {
        set((state) => ({
          clockLocations: state.clockLocations.filter((loc) => loc.id !== id),
        }))
      },

      addWeatherLocation: (location) => {
        set((state) => ({
          weatherLocations: [...state.weatherLocations, location],
        }))
      },

      removeWeatherLocation: (id) => {
        set((state) => ({
          weatherLocations: state.weatherLocations.filter((loc) => loc.id !== id),
        }))
      },

      addIncomeCategory: (category) => {
        set((state) => {
          if (!state.incomeCategories.includes(category)) {
            return {
              incomeCategories: [...state.incomeCategories, category],
            }
          }
          return state
        })
      },

      removeIncomeCategory: (category) => {
        set((state) => ({
          incomeCategories: state.incomeCategories.filter((c) => c !== category),
        }))
      },

      addExpenseCategory: (category) => {
        set((state) => {
          if (!state.expenseCategories.includes(category)) {
            return {
              expenseCategories: [...state.expenseCategories, category],
            }
          }
          return state
        })
      },

      removeExpenseCategory: (category) => {
        set((state) => ({
          expenseCategories: state.expenseCategories.filter((c) => c !== category),
        }))
      },
    }),
    {
      name: 'settings',
      storage: createAsyncStorage(),
    }
  )
)

// Load settings from Chrome Storage on startup (rehydration is handled by Zustand persist)
export const initializeSettings = async (): Promise<void> => {
  try {
    console.log('✅ Settings initialized via Zustand persist')
  } catch (error) {
    console.error('❌ Failed to initialize settings:', error)
  }
}
